import {IconButton} from '@albert-io/atomic';
import classnames from 'classnames';
import {Set} from 'immutable';
import React, {useContext} from 'react';

import {callTargetedAction} from 'client/framework';
import CorrectnessIndicatorWrapper from 'client/QuestionTypes/common/CorrectnessIndicatorWrapper/CorrectnessIndicatorWrapper';
import {OptionEliminatorContext} from 'components/PracticeView/OptionEliminator/OptionEliminator.react';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';

import multipleChoiceQuestionActions from './MultipleChoiceQuestion.actions';

interface Props {
  index: number;
  isCorrectAnswer: boolean;
  isEliminated: boolean;
  isGuessSubmitted: boolean;
  isSelected: boolean;
  isSelectMultiple: boolean;
  option: Record<string, any>;
  targetStore: string;
  isMCQRestylingEnabled?: boolean;
  questionId: string;
  // translation props
  translatedOptionText?: string;
  showTranslation?: boolean;
  // elimination props (for legacy wrapper)
  onEliminate?: () => void;
}

export default function MultipleChoiceOption({
  index,
  isCorrectAnswer,
  isEliminated,
  isGuessSubmitted,
  isSelected,
  isSelectMultiple,
  option,
  targetStore,
  showTranslation = false,
  translatedOptionText,
  isMCQRestylingEnabled,
  questionId,
  onEliminate
}: Props) {
  const optionId = option.getId();
  const {getQuestionState, hideEliminatedOptions, updateQuestionState} =
    useContext<any>(OptionEliminatorContext);

  // Use eliminator context if available and restyling is enabled
  const useBuiltInEliminator = isMCQRestylingEnabled;

  // Get elimination state from context when using built-in eliminator
  const isEliminatedFromContext = useBuiltInEliminator
    ? getQuestionState(questionId)?.includes(optionId) || false
    : false;

  const finalIsEliminated = useBuiltInEliminator ? isEliminatedFromContext : isEliminated;
  const isEliminatorEnabled = useBuiltInEliminator ? true : !!onEliminate;
  const shouldHideEliminatedOptions = useBuiltInEliminator ? hideEliminatedOptions : false;

  // Don't render if option is eliminated and should be hidden
  if (finalIsEliminated && shouldHideEliminatedOptions) {
    return null;
  }

  const wrapperClass = classnames('mcq-option', {
    'mcq-option--selected': isSelected,
    'mcq-option--unselected': !isSelected,
    'mcq-option--pre-submission': !isGuessSubmitted,
    'mcq-option--post-submission': isGuessSubmitted,
    'mcq-option--eliminated': finalIsEliminated && !isGuessSubmitted,
    'mcq-option--checkbox': isSelectMultiple,
    'mcq-option--restyled': isMCQRestylingEnabled
  });

  function handleClick() {
    if (isGuessSubmitted || finalIsEliminated) {
      return;
    }

    callTargetedAction({
      name: multipleChoiceQuestionActions.SELECT_OPTION,
      payload: option.getId(),
      targetStore
    });
  }

  function handleEliminate() {
    if (useBuiltInEliminator) {
      // Use eliminator context logic
      if (!finalIsEliminated && onEliminate) {
        onEliminate();
      }
      updateQuestionState(questionId, (questionState = Set()) => {
        return questionState.includes(optionId)
          ? questionState.delete(optionId)
          : questionState.add(optionId);
      });
    } else if (onEliminate) {
      // Fallback to legacy elimination
      onEliminate();
    }
  }

  return (
    <div
      data-testid={`mcq-option-${index}`}
      className={classnames('mcq-option-accessible-wrapper', {
        'mcq-option-eliminator': isMCQRestylingEnabled && isEliminatorEnabled
      })}
    >
      <input
        className='mcq-option__hidden-input'
        type={isSelectMultiple ? 'checkbox' : 'radio'}
        onChange={handleClick}
        checked={isSelected}
        id={`input-${optionId}`}
      />
      <CorrectnessIndicatorWrapper
        as='label'
        // @ts-expect-error htmlFor is a valid prop for label elements
        htmlFor={`input-${optionId}`}
        className={wrapperClass}
        isGuessSubmitted={isGuessSubmitted}
        isCorrectAnswer={isCorrectAnswer}
        isSelected={isSelected}
        isMCQRestylingEnabled={isMCQRestylingEnabled}
      >
        <div
          className={classnames('mcq-option__letter-wrapper', {
            'mcq-option__letter-wrapper--checkbox': isSelectMultiple,
            'mcq-option__letter-wrapper--restyled': isMCQRestylingEnabled
          })}
        >
          <span className='mcq-option__letter'>
            {String.fromCharCode('A'.charCodeAt(0) + index)}
          </span>
        </div>
        <MarkdownRendererV2
          className='mcq-option__content'
          text={showTranslation ? translatedOptionText : option.getValue()}
        />
        {isMCQRestylingEnabled && isEliminatorEnabled && !isGuessSubmitted && (
          <IconButton
            className='mcq-option__eliminate-btn !ml-auto !shrink-0'
            size='s'
            color='secondary'
            icon={finalIsEliminated ? 'eye-slash' : 'eye'}
            iconStyle='regular'
            label={finalIsEliminated ? 'Restore option' : 'Eliminate option'}
            onClick={handleEliminate}
          />
        )}
      </CorrectnessIndicatorWrapper>
    </div>
  );
}
